import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Nav, NavItem } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink, withRouter } from 'react-router-dom';
import NavbarVerticalMenuItem from './NavbarVerticalMenuItem';

const isExternalRouteActive = href => {
  if (!href || typeof window === 'undefined') {
    return false;
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const normalizedHref = href.replace(window.location.origin, '');

  return currentPath === normalizedHref || currentPath.indexOf(normalizedHref) === 0;
};

const hasChildren = route => Array.isArray(route.children) && route.children.length > 0;
const getRouteTarget = route => {
  if (route.href) {
    return route.href;
  }

  if (route.to && route.search) {
    return {
      pathname: route.to,
      search: route.search
    };
  }

  return route.to || null;
};
const getFallbackRouteTarget = route => getRouteTarget(route) || getRouteTarget(route.children?.[0] || {}) || '#!';
const mergeClassName = (...values) => values.filter(Boolean).join(' ');
const normalizeSearch = search => {
  const params = new URLSearchParams(search || '');
  return params.toString();
};

const isRouteActive = (route, location) => {
  const pathname = location.pathname;
  const currentSearch = normalizeSearch(location.search);

  if (route.to) {
    if (route.search) {
      return pathname === route.to && currentSearch === normalizeSearch(route.search);
    }

    if (route.to === '/') {
      return pathname === '/';
    }

    if (pathname === route.to || pathname.indexOf(`${route.to}/`) === 0) {
      return true;
    }
  }

  if (route.href && isExternalRouteActive(route.href)) {
    return true;
  }

  return hasChildren(route) && route.children.some(child => isRouteActive(child, location));
};

const NavbarVerticalMenu = ({ routes, location, handleNavbarVerticalCollapse, isSidebarCollapsed }) => {
  const [opened, setOpened] = useState(null);

  useEffect(() => {
    let openedDropdown = null;

    routes.forEach((route, index) => {
      if (hasChildren(route) && isRouteActive(route, location)) {
        openedDropdown = index;
      }
    });

    setOpened(openedDropdown);
  }, [location, routes]);

  const toggleOpened = (e, index) => {
    e.preventDefault();
    return setOpened(opened === index ? null : index);
  };

  const renderRouteLink = (route, content, extraProps = {}, allowChildFallback = false) => {
    const target = allowChildFallback ? getFallbackRouteTarget(route) : getRouteTarget(route);
    const isExternal = Boolean(route.href);
    const isActive = isRouteActive(route, location);
    const { className, onClick, ...restProps } = extraProps;
    const linkClassName = mergeClassName('nav-link', className);

    const handleClick = event => {
      if (typeof onClick === 'function') {
        onClick(event);
      }

      handleNavbarVerticalCollapse();
    };

    if (isExternal) {
      return (
        <a
          href={target}
          className={`${linkClassName}${isActive ? ' active' : ''}`}
          onClick={handleClick}
          title={route.name}
          {...restProps}
        >
          {content}
        </a>
      );
    }

    return (
      <NavLink
        className={linkClassName}
        to={target}
        exact={route.exact}
        onClick={handleClick}
        title={route.name}
        {...restProps}
      >
        {content}
      </NavLink>
    );
  };

  return routes.map((route, index) => {
    const routeHasChildren = hasChildren(route);
    const isOpen = opened === index;
    const isActive = isRouteActive(route, location);

    if (!routeHasChildren) {
      return (
        <NavItem key={index}>
          {renderRouteLink(route, <NavbarVerticalMenuItem route={route} />)}
        </NavItem>
      );
    }

    if (isSidebarCollapsed) {
      return (
        <NavItem key={index}>
          {renderRouteLink(route, <NavbarVerticalMenuItem route={route} />, {}, true)}
        </NavItem>
      );
    }

    return (
      <NavItem key={index}>
        <div className="med-navbar-vertical-group-row">
          <button
            type="button"
            onClick={e => toggleOpened(e, index)}
            className={mergeClassName('nav-link', 'med-navbar-vertical-group-link', isActive ? 'active' : '')}
            aria-expanded={isOpen}
            title={route.name}
          >
            <NavbarVerticalMenuItem route={route} />
          </button>
          <button
            type="button"
            onClick={e => toggleOpened(e, index)}
            className={`med-navbar-vertical-group-toggle${isOpen ? ' is-open' : ''}`}
            aria-expanded={isOpen}
            aria-label={`${route.name} menyusini ochish`}
            title={`${route.name} menyusini ochish`}
          >
            <FontAwesomeIcon icon="chevron-down" />
            <span className="sr-only">{route.name} menyusini ochish</span>
          </button>
        </div>
        <Collapse isOpen={isOpen}>
          <Nav>
            <NavbarVerticalMenu
              routes={route.children}
              location={location}
              handleNavbarVerticalCollapse={handleNavbarVerticalCollapse}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          </Nav>
        </Collapse>
      </NavItem>
    );
  });
};

NavbarVerticalMenu.propTypes = {
  routes: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  isSidebarCollapsed: PropTypes.bool
};

NavbarVerticalMenu.defaultProps = {
  isSidebarCollapsed: false
};

export default withRouter(NavbarVerticalMenu);
